import axios, { axiosPublic } from '../axiosInterceptor';
import { ModuleInfoResponse } from '../models/ModuleInfoResponse';
import { AxiosResponse, GenericAbortSignal } from 'axios';
import { MenuResponse } from '../models/MenuResponse';
import { WidgetData } from '../models/Widget/WidgetData';
import {
  DataResponse,
  DynamicDataParams,
  DynamicDataRequest,
  DynamicDataResponse,
  ExecuteActionParams,
  Page,
} from '../models/DynamicDataParams';
import { RequestIdentity } from '../models/RequestIdentity';
import { clientId, clientSecret } from './auth';
import { AuthConfig } from '../models/auth/AuthConfig';
import { SelectableElement } from '../components/Widgets/Declaration/DropdownWidget/models/item';
import { DynamicDataEndpointType } from '../models/ViewEditorDataDynamicDataType';
import { FilterDefinition } from '../components/Filters/models/FilterDefinition';
import { ViewResponse } from '../models/viewResponse/ViewResponse';
import { ActionExecutionResult } from '../models/Widget/actions/ActionExecutionResult';
import { generateEqualFilter } from '../utils/generateFilter';
import { ModuleVersionResponse } from '../models/ModuleVersionResponse';
import { ModuleHealthStatusResponse } from '../models/ModuleHealthStatusResponse';
import { UpdateModuleRequest } from '../models/UpdateModuleRequest';
import { OAuth2Claims } from '../models/auth/OAuth2Claims';
import { ViewStateUrlContext } from '../utils/getViewStateFromUrl';
import { ChangePasswordData, ChangePasswordResponse, ProfileData } from '../models/profile';

const BASE_PATH: string = '/pbs/api/v1/modules';

//region Module Controller
/**
 * Список модулей
 */
export const getModules = (): Promise<AxiosResponse<Array<ModuleInfoResponse>>> => {
  return axios.get(BASE_PATH);
};

/**
 * Информация о модуле
 */
export const getModuleInfo = (moduleName: string): Promise<AxiosResponse<ModuleInfoResponse>> => {
  return axios.get(`${BASE_PATH}/${moduleName}`);
};

/**
 * Информация о сборке модуля
 */
export const getModuleVersion = (
  moduleName: string,
): Promise<AxiosResponse<ModuleVersionResponse>> => {
  return axios.get(`${BASE_PATH}/${moduleName}/version`);
};

/**
 * Логи модуля. Для мастера имя модуля не нужно указывать
 */
export const getModuleLogs = (moduleName: string | null): Promise<AxiosResponse<any>> => {
  if (moduleName) {
    return axios.get(`${BASE_PATH}/logs?module_name=${moduleName}`);
  }
  return axios.get(`${BASE_PATH}/logs`);
};

/**
 * Жив ли модуль
 */
export const getModuleHealth = (
  moduleName: string,
): Promise<AxiosResponse<ModuleHealthStatusResponse>> => {
  return axios.get(`${BASE_PATH}/${moduleName}/health`);
};

/**
 * Обновить основную информацию о модуле
 */
export const updateModule = (
  moduleName: string,
  body: UpdateModuleRequest,
): Promise<AxiosResponse<ActionExecutionResult>> => {
  return axios.put(`${BASE_PATH}/${moduleName}`, body);
};

//endregion

//region Auth
export const getAuthConfig = (): Promise<AxiosResponse<AuthConfig>> => {
  return axiosPublic.get(`${BASE_PATH}/auth`);
};

export const getToken = (code: string) => {
  const redirect_uri = window.location.origin + '/callback';
  const params = new URLSearchParams();
  params.set('grant_type', 'authorization_code');
  params.set('redirect_uri', redirect_uri);
  params.set('code', code);

  return axios.post('/oauth2/token', params.toString(), {
    headers: { 'Content-type': 'application/x-www-form-urlencoded' },
  });
};

export const fetchRefreshToken = (refreshToken: string) => {
  const params = new URLSearchParams();
  params.set('grant_type', 'refresh_token');
  params.set('refresh_token', refreshToken);
  return axios.post('/oauth2/token', params.toString(), {
    headers: { 'Content-type': 'application/x-www-form-urlencoded' },
  });
};

export const getUserClaims = (token: string): Promise<AxiosResponse<OAuth2Claims>> => {
  const data = {
    token: token,
    'client-id': clientId,
    'client-secret': clientSecret,
  };

  return axios.post<OAuth2Claims>(`/oauth2/introspect`, data, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  });
};
//endregion

//region User Change Settings

export const changePassword = async (
  changeData: ChangePasswordData,
): Promise<ChangePasswordResponse> => {
  try {
    const requestData = {
      oldPassword: changeData.current,
      newPassword: changeData.new,
      confirmPassword: changeData.confirm,
      'client-id': clientId,
    };

    const response = await axios.post<ChangePasswordResponse>(
      `${BASE_PATH}/USER_MODULE/base/executeAction`,
      requestData,
      {
        headers: {
          'Content-Type': 'application/json',
        },
        params: {
          actionName: 'changePassword',
          type: 'UserDataView',
          configName: 'UserDataView',
        },
      },
    );

    return response.data;
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 401) {
        throw new Error('Текущий пароль неверен');
      } else if (error.response?.status === 400) {
        throw new Error(error.response.data?.message || 'Некорректные данные');
      }
    }
    throw new Error('Не удалось изменить пароль. Попробуйте позже.');
  }
};

export const updateProfile = async (data: ProfileData): Promise<ProfileData> => {
  try {
    const response = await axios.post(`${BASE_PATH}/USER_MODULE/base/executeAction`, data, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
      params: {
        actionName: 'updateProfile',
        type: 'UserDataView',
        configName: 'UserDataView',
      },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update profile');
    }

    return response.data;
  } catch (error: any) {
    if (error.response) {
      switch (error.response.status) {
        case 409:
          throw new Error('Пользователь с таким email уже существует');
        case 401:
          throw new Error('Сессия истекла. Пожалуйста, войдите снова.');
        case 403:
          throw new Error('Недостаточно прав для изменения профиля');
        case 400:
          throw new Error(error.response.data.message || 'Некорректные данные');
        case 404:
          throw new Error('Профиль не найден');
        case 500:
          throw new Error('Внутренняя ошибка сервера. Попробуйте позже.');
        default:
          throw new Error(`Ошибка сервера: ${error.response.status}`);
      }
    } else if (error.request) {
      throw new Error('Нет ответа от сервера. Проверьте подключение к интернету.');
    } else {
      throw new Error('Ошибка при отправке запроса: ' + error.message);
    }
  }
};
//endregion

//region Base Contract

/**
 Получение данных для формы или страницы (при изменении сущности при помощи формы
 или при открытии страницы после нажатию на дерево)
 */
export const getData = <T extends WidgetData = WidgetData>(
  moduleKey: string,
  params: RequestIdentity,
  abortSignal?: GenericAbortSignal,
): Promise<T> => {
  return axios
    .get(`${BASE_PATH}/${moduleKey}/base/data`, { params, signal: abortSignal || undefined })
    .then((response) => {
      return response?.data;
    });
};

/**
 * Получение представления
 */
export const getConfig = (
  moduleKey: string,
  params: RequestIdentity,
  abortSignal?: GenericAbortSignal,
): Promise<ViewResponse> => {
  return axios
    .get(`${BASE_PATH}/${moduleKey}/base/config`, {
      params: params,
      signal: abortSignal || undefined,
    })
    .then((response) => response?.data);
};

/**
 Получение данных для отдельных виджетов (дерево, таблица)
 */
export const getDynamicData = <T extends DataResponse = DataResponse>(
  moduleKey: string,
  params: DynamicDataParams,
  body: DynamicDataRequest | null,
  abortSignal?: AbortSignal,
): Promise<DynamicDataResponse<T>> => {
  const data = body || {};
  return axios
    .post(`${BASE_PATH}/${moduleKey}/base/dynamicData`, data, { params, signal: abortSignal })
    .then((response) => response.data);
};

/**
 * Создание, изменение данных при помощи формы
 */
export const executePageAction = (
  moduleKey: string,
  params: ExecuteActionParams,
  body: any,
): Promise<ActionExecutionResult> => {
  const data = body || {};
  return axios.post(`${BASE_PATH}/${moduleKey}/base/executeAction`, data, { params });
};

export const downloadFile = (
  moduleKey: string,
  params: ExecuteActionParams,
  body: any,
): Promise<any> => {
  const data = body || {};
  return axios.post(`${BASE_PATH}/${moduleKey}/base/download`, data, {
    params,
    responseType: 'blob',
  });
};

export const getMenus = (
  moduleName: string,
  parentId?: number,
): Promise<AxiosResponse<Array<MenuResponse>>> => {
  return axios.get(`${BASE_PATH}/${moduleName}/base/menus`, {
    params: parentId ? { parentId } : {},
  });
};

/**
 * Получение uuid для построения конфигурации страницы и хлебных крошек
 */
export const getLink = (uuid: string): Promise<AxiosResponse<ViewStateUrlContext>> => {
  return axios.get('/pbs/api/v1/link', {
    params: { uuid: encodeURIComponent(uuid) },
  });
};

/**
 * Генерация uuid ссылки для ее передачи
 */
export const createLink = (context: string): Promise<AxiosResponse<string>> => {
  return axios.put('/pbs/api/v1/link', { context: context }, {});
};

//endregion

export const getFiltersCollection = (
  moduleKey: string,
  type: string,
  collectionName: string,
  page: Page,
): Promise<DynamicDataResponse<FilterDefinition>> => {
  const configName = 'CollectionFilterPage';
  const dynamicDataParams: DynamicDataParams = {
    type: configName,
    id: '1',
    configName,
    fieldName: 'getCollectionFilters',
  };

  const requestBody: DynamicDataRequest = {
    filters: [generateEqualFilter('type', type), generateEqualFilter('collection', collectionName)],
    orders: [],
    page: page,
  };

  return getDynamicData<FilterDefinition>(moduleKey, dynamicDataParams, requestBody);
};

/** Обёртка над getDynamicData для получения списка опшенов селектов.
 Юзать для запросов ТОЛЬКО ВНУТРИ КОНСТРУКТОРА **/
export const getViewEditorDynamicData = (
  moduleKey: string,
  formContextId: string,
  requestBody: DynamicDataRequest,
  endpointType: DynamicDataEndpointType,
  type: string = 'ViewEditorDynamicDataView',
) => {
  const requestParams: DynamicDataParams = {
    type,
    fieldName: endpointType,
    configName: type,
    id: formContextId,
  };

  return getDynamicData<SelectableElement>(moduleKey, requestParams, requestBody);
};

export const getAvailableThemes = () => {
  return axios.get('/pbs/api/v1/modules/style/all');
};

export const getTheme = (uuid: string) => {
  return axios.get(`/pbs/api/v1/modules/style/${uuid}`);
};

export const updateTheme = (uuid: string, payload: unknown) => {
  return axios.put(`/pbs/api/v1/modules/style/${uuid}`, payload);
};

export const setDefaultUserTheme = (uuid: string) => {
  return axios.put(`/pbs/api/v1/modules/style/current/${uuid}`);
};

export const getDefaultUserTheme = (favTheme: 'light' | 'dark') => {
  return axios.get(`/pbs/api/v1/modules/style`, { headers: { 'X-Theme': favTheme } });
};

export const saveTheme = (payload: unknown) => {
  return axios.post(`/pbs/api/v1/modules/style`, payload);
};

export const deleteTheme = (uuid: string) => {
  return axios.delete(`/pbs/api/v1/modules/style/${uuid}`);
};
